import React, { useState, useEffect } from "react";
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";

/** Product gallery with zoom-on-hover and full-screen lightbox. */
export default function ProductGallery({ images = [], title = "" }) {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [zoom, setZoom] = useState({ on: false, x: 50, y: 50 });

  useEffect(() => { setActive(0); }, [images?.length]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [lightbox]);

  if (!images || images.length === 0) {
    return (
      <div className="flex aspect-[4/5] items-center justify-center rounded-md bg-[#E8E2D2] font-display text-3xl text-[#2D2823]/40">
        No image
      </div>
    );
  }

  const prev = () => setActive((i) => (i - 1 + images.length) % images.length);
  const next = () => setActive((i) => (i + 1) % images.length);

  const onHoverMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoom({ on: true, x, y });
  };

  return (
    <div className="flex min-w-0 gap-4" data-testid="product-gallery">
      {images.length > 1 && (
        <div className="hidden flex-col gap-2 md:flex">
          {images.slice(0, 6).map((url, i) => (
            <button
              key={url}
              onClick={() => setActive(i)}
              className={`h-20 w-20 overflow-hidden rounded-sm border transition-colors ${active === i ? "border-[#1A1A1A]" : "border-[#DDD6C5] hover:border-[#2D2823]"}`}
              aria-label={`View image ${i + 1}`}
              data-testid={`thumb-${i}`}
            >
              <img src={url} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="group/gallery relative min-w-0 flex-1">
        <button
          onClick={() => setLightbox(true)}
          onMouseMove={onHoverMove}
          onMouseLeave={() => setZoom({ on: false, x: 50, y: 50 })}
          className="group relative block aspect-[4/5] w-full overflow-hidden rounded-md bg-[#E8E2D2]"
          aria-label="Open fullscreen gallery"
          data-testid="open-lightbox"
        >
          <img
            src={images[active]}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-300 ease-out"
            style={zoom.on ? {
              transformOrigin: `${zoom.x}% ${zoom.y}%`,
              transform: "scale(1.5)",
            } : undefined}
          />
          <div className="absolute right-3 top-3 rounded-full bg-white/80 p-2 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
            <ZoomIn size={16} />
          </div>
        </button>

        {/* Prev / Next arrows on main image (always visible on mobile, fade-in on hover desktop) */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Previous image"
              data-testid="gallery-prev"
              className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#1A1A1A] shadow-md transition-all hover:scale-105 hover:bg-white md:opacity-0 md:group-hover/gallery:opacity-100"
            >
              <ChevronLeft size={20} strokeWidth={1.8} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Next image"
              data-testid="gallery-next"
              className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#1A1A1A] shadow-md transition-all hover:scale-105 hover:bg-white md:opacity-0 md:group-hover/gallery:opacity-100"
            >
              <ChevronRight size={20} strokeWidth={1.8} />
            </button>
            {/* Image counter pill — always visible bottom-left */}
            <div
              className="store-nums pointer-events-none absolute bottom-3 left-3 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur"
              data-testid="gallery-counter"
            >
              {active + 1} / {images.length}
            </div>
          </>
        )}

        {/* Mobile dots */}
        {images.length > 1 && (
          <div className="mt-3 flex gap-1.5 md:hidden">
            {images.slice(0, 6).map((_, i) => (
              <button key={i} onClick={() => setActive(i)} aria-label={`Go to image ${i + 1}`} className={`h-1.5 flex-1 rounded-full transition-colors ${active === i ? "bg-[#1A1A1A]" : "bg-[#DDD6C5]"}`} />
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0A0A0A]/90 backdrop-blur-sm" onClick={() => setLightbox(false)} data-testid="lightbox">
          <button onClick={() => setLightbox(false)} className="absolute right-6 top-6 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" aria-label="Close"><X size={22} /></button>
          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-6 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" aria-label="Previous"><ChevronLeft size={22} /></button>
              <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-6 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20" aria-label="Next"><ChevronRight size={22} /></button>
            </>
          )}
          <img
            src={images[active]}
            alt={title}
            className="max-h-[88vh] max-w-[92vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-1 text-xs text-white store-nums">
            {active + 1} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
}
